import React from 'react';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import { Box, Tooltip } from '@mui/material';
import { LockClockOutlined } from '@mui/icons-material';

interface FeatureBoxProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  buttons: React.ReactNode;
  variant?: 'default' | 'large';
  lockedItems?: React.ReactNode;
  lockedItemsTitle?: string;
}

const useStyles = makeStyles<Theme>((theme) => ({
  featureBox: {
    padding: '24px',
    borderRadius: '6px',
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[2],
    height: '100%',
    minHeight: '300px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    border: '1px solid rgba(241, 198, 43, 0.3)',
    gap: '16px',
    flex: 1,
  },
  featureBoxLarge: {
    minHeight: '280px',
  },
  icon: {
    color: '#FFD700',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '24px',
    '& .MuiSvgIcon-root': {
      fontSize: '24px',
    },
    '& img': {
      height: '24px',
    },
  },
  title: {
    color: theme.palette.text.primary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: 0,
  },
  description: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    flexWrap: 'wrap',
    justifyContent: 'center',
    width: '100%',
  },
  buttonRow: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    justifyContent: 'center',
    '& > *': {
      display: 'flex',
      gap: '8px',
      flexWrap: 'wrap',
      justifyContent: 'center',
    },
    '& a': {
      textDecoration: 'none',
    },
    '& .MuiButton-root': {
      textTransform: 'none',
      padding: '6px 12px',
      fontSize: '0.875rem',
      minWidth: '140px',
      '& .MuiSvgIcon-root': {
        fontSize: '1rem',
        '&:first-of-type': {
          marginRight: '8px',
        },
        '&:last-of-type': {
          marginLeft: '8px',
        },
      },
      '& .fab': {
        fontSize: '1rem',
        marginRight: '8px',
      },
      '&.Mui-disabled': {
        opacity: 0.6,
      },
    },
  },
  lockedItemsContainer: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    justifyContent: 'center',
    width: '100%',
    '& .buttonRow': {
      display: 'flex',
      gap: '12px',
      flexWrap: 'wrap',
      justifyContent: 'center',
      '& > *': {
        display: 'flex',
        gap: '8px',
      },
      '& .MuiButton-root': {
        textTransform: 'none',
        padding: '6px 12px',
        fontSize: '0.875rem',
        minWidth: '140px',
        '& .MuiSvgIcon-root': {
          fontSize: '1rem',
          '&:first-of-type': {
            marginRight: '8px',
          },
          '&:last-of-type': {
            marginLeft: '8px',
          },
        },
        '& .fab': {
          fontSize: '1rem',
          marginRight: '8px',
        },
        '&.Mui-disabled': {
          opacity: 0.6,
        },
      },
    },
  },
}));

const FeatureBox: React.FC<FeatureBoxProps> = ({
  icon,
  title,
  description,
  buttons,
  variant = 'default',
  lockedItems,
  lockedItemsTitle,
}) => {
  const classes = useStyles();

  return (
    <Box className={`${classes.featureBox} ${variant === 'large' ? classes.featureBoxLarge : ''}`}>
      <Box className={classes.icon}>{icon}</Box>
      <h4 className={classes.title}>{title}</h4>
      <Box className={classes.description}>{description}</Box>
      <Box className={classes.buttonsContainer}>
        {Array.isArray(buttons) ? (
          buttons.map((row, index) => (
            <Box key={index} className={classes.buttonRow}>
              {row}
            </Box>
          ))
        ) : (
          <Box className={classes.buttonRow}>{buttons}</Box>
        )}
      </Box>
      {lockedItems && (
        <>
          <LockClockOutlined sx={{ opacity: 0.7, marginY: '12px' }} />
          <Tooltip title={lockedItemsTitle || ''} arrow placement="top">
            <Box className={classes.lockedItemsContainer}>{lockedItems}</Box>
          </Tooltip>
        </>
      )}
    </Box>
  );
};

export default FeatureBox;
